import { inject, injectable } from "tsyringe";
import { autoBind } from "src/shared/decorators/autobind";
import { LifePolicy } from "src/shared/models/life-policy.model";
import { PgaPolicy } from "src/shared/models/pga-policy.model";
import { RentalPolicy } from "src/shared/models/rental-policy.model";
import { VehiclePolicy } from "src/shared/models/vehicle-policy.model";
import { ScratchGame } from "src/containers/wellbeing/rewards/domain/models/scratch-game";
import { WellbeingRewardsService } from "src/containers/wellbeing/rewards/services/rewards-service/rewards-service";
import { VehicularAssistanceService } from "src/containers/vehicular-assistance/core/services/vehicular-assistance/vehicular-assistance";
import { ETNABenefit } from "src/containers/wellbeing/discounts/models/etna-benefit/etna-benefit";
import { HealthAtHomeBenefit } from "src/containers/wellbeing/servings/models/health-at-home-benefit/health-at-home-benefit";
import { MultiventajasBenefit } from "src/containers/wellbeing/discounts/models/multiventajas-benefit/multiventajas-benefit";

@injectable()
export class WellbeingService {
    constructor(
        @inject("WellbeingRewardsService")
        private rewardsService: WellbeingRewardsService,
        @inject("VehicularAssistanceService")
        private vaService: VehicularAssistanceService
    ) {}

    @autoBind
    private _getPoliciesForETNA(policies: PgaPolicy[]): VehiclePolicy[] {
        return policies.filter(
            (p) => p instanceof VehiclePolicy
        ) as VehiclePolicy[];
    }

    @autoBind
    private _getPoliciesForHH(policies: PgaPolicy[]): PgaPolicy[] {
        const lifePolicies = policies.filter((p) => p instanceof LifePolicy);
        const rentalPolicies = policies.filter(
            (p) => p instanceof RentalPolicy
        );
        return [...lifePolicies, ...rentalPolicies];
    }

    @autoBind
    hasETNABenefit(policies: PgaPolicy[]): boolean {
        const vehiclePolicies = this._getPoliciesForETNA(policies);
        return ETNABenefit.hasAccess(vehiclePolicies);
    }

    @autoBind
    hasHHBenefit(policies: PgaPolicy[]): boolean {
        const hhPolicies = this._getPoliciesForHH(policies);
        return HealthAtHomeBenefit.hasAccess(hhPolicies);
    }

    @autoBind
    hasMultiventajasBenefit(policies: PgaPolicy[]): boolean {
        // TODO - maybe need to restore after a/b testing
        return MultiventajasBenefit.hasAccess(policies) && false;
    }

    @autoBind
    hasVehicularAssistanceBenefit(policies: Array<PgaPolicy>): boolean {
        return this.vaService.hasAccess(policies);
    }

    @autoBind
    hasSomeBenefit(policies: PgaPolicy[], games: Array<ScratchGame>): boolean {
        const isFlagEnabled = process.env.REACT_APP_WELLNESS_FEATURE === "on";
        const hasMultiBenefit = this.hasMultiventajasBenefit(policies);
        const hasETNABenefit = this.hasETNABenefit(policies);
        const hasHHBenefit = this.hasHHBenefit(policies);
        const hasVehicularAssistanceBenefit =
            this.hasVehicularAssistanceBenefit(policies);
        const hasRewardsBenefit = this.rewardsService.hasAccess(
            policies,
            games
        );

        return (
            isFlagEnabled &&
            (hasMultiBenefit ||
                hasVehicularAssistanceBenefit ||
                hasETNABenefit ||
                hasHHBenefit ||
                hasRewardsBenefit)
        );
    }
}
